import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Copy, Check, LogOut, Settings as SettingsIcon, Ruler, Layers, Stethoscope, Wallet, FileText, Compass, Newspaper, Camera, Trophy, Droplet, Youtube, Sparkles, BookOpen, Plane, Users, Cpu, Languages, Gamepad2, Heart, Github, Linkedin, MessagesSquare, Brain, TrendingUp, Inbox, VenetianMask, CheckCircle2, ScanLine, Moon, Target, Users2, Radar, Contact, Gift,
} from 'lucide-react'
import { APP_VERSION, APP_BUILD } from '../version'
import { isOwner, hasOwnerAccess } from '../lib/owner'
import { useAuth } from '../contexts/AuthContext'
import Avatar from './Avatar'
import { useProfile } from '../hooks/useProfile'
import { useUnreadMessages } from '../hooks/useUnreadMessages'
import { useConfirm } from '../contexts/ConfirmDialogContext'
import { computeAge, daysUntilBirthday } from '../lib/dateUtils'
import { cn } from '../lib/utils'

// Animation timings — kept on a single shared constant so the
// container's slide and the scrim's fade run for the exact same
// window. Anything in this range feels native on iOS / Android.
const ENTER_MS = 260
const EXIT_MS = 220
// Staggered cascade for the inner rows. Tiny per-row delay so the
// list looks like it "settles in" from the bottom upward as the
// sheet finishes its slide-up. Capped so a long list doesn't drag.
const ROW_STAGGER_MS = 22

// Bottom-anchored profile sheet, replacing the previous top-right
// dropdown. The trigger lives in the bottom tab bar; this is
// controlled by `open` + `onClose` from the parent.
//
// Animation model: the component stays mounted for one paint after
// `open` flips to false so the exit transition can play. A pair of
// refs/states tracks mount + visible separately:
//   mounted  → in the DOM
//   visible  → has the open-state styles applied
// open=true  → mount → next frame, set visible=true (transitions
//              kick in)
// open=false → set visible=false, schedule unmount after EXIT_MS
export default function ProfileSheet({ open, onClose }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { profile } = useProfile()
  const unreadMessages = useUnreadMessages()
  const confirm = useConfirm()
  const [copied, setCopied] = useState(false)

  // Two-phase mount/visible state so exit transitions get to play
  // before React rips the node out of the DOM.
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)
  const exitTimerRef = useRef(null)

  // Drag-to-dismiss: grab the handle and pull the whole sheet down,
  // native bottom-sheet style. dragY is the current downward offset
  // (px); released past DISMISS_PX closes, otherwise it snaps back.
  const [dragY, setDragY] = useState(0)
  const dragRef = useRef({ startY: 0, active: false })
  // True briefly after a drag so the synthesized click the browser
  // fires on release (which can land on the scrim once the sheet has
  // snapped back up) doesn't trigger a close.
  const justDraggedRef = useRef(false)
  const DISMISS_PX = 150

  const onDragStart = (e) => {
    const y = e.touches ? e.touches[0].clientY : e.clientY
    dragRef.current = { startY: y, active: true }
  }
  const onDragMove = (e) => {
    if (!dragRef.current.active) return
    const y = e.touches ? e.touches[0].clientY : e.clientY
    const dy = y - dragRef.current.startY
    if (Math.abs(dy) > 5) justDraggedRef.current = true
    // Down follows the finger 1:1; a small up-drag rubber-bands.
    setDragY(dy > 0 ? dy : dy * 0.25)
  }
  const onDragEnd = () => {
    if (!dragRef.current.active) return
    dragRef.current.active = false
    const shouldClose = dragY > DISMISS_PX
    setDragY(0)
    if (shouldClose) {
      onClose?.()
    } else if (justDraggedRef.current) {
      // Snapping back — eat the trailing synthesized click.
      setTimeout(() => { justDraggedRef.current = false }, 400)
    }
  }

  const handleScrimClick = () => {
    if (justDraggedRef.current) { justDraggedRef.current = false; return }
    onClose?.()
  }
  useEffect(() => {
    if (open) {
      // Cancel a pending exit if the user re-opens mid-transition.
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
      setMounted(true)
      // Next frame so the initial off-screen style commits before
      // we flip to the on-screen one — otherwise the browser sees a
      // single style change and skips the animation.
      const raf = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(raf)
    }
    setVisible(false)
    exitTimerRef.current = setTimeout(() => setMounted(false), EXIT_MS)
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(user?.id || '')
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* ignore */
    }
  }

  const handleSignOut = async () => {
    onClose?.()
    const ok = await confirm({
      title: t('phoenixTracker.auth.confirmSignOut'),
      description: t('phoenixTracker.auth.confirmSignOutDesc'),
      confirmLabel: t('phoenixTracker.auth.signOut'),
      variant: 'destructive',
      icon: LogOut,
    })
    if (ok) await signOut()
  }

  // Assistant sheet lives at App-level (see App.jsx → AssistantSheet)
  // because rendering it inside this drawer meant unmounting the
  // drawer killed the assistant before it could slide in. We just
  // fire a window event here; the App listens and opens the sheet.
  const handleAssistant = () => {
    onClose?.()
    setTimeout(() => {
      window.dispatchEvent(new Event('phoenix:open-assistant'))
    }, EXIT_MS + 20)
  }

  const go = (cb) => () => {
    onClose?.()
    cb()
  }

  const handleOpenSettings = go(() => navigate('/settings'))
  const handleCompleteProfile = go(() => {
    navigate('/')
    window.dispatchEvent(new Event('phoenix:edit-profile'))
  })
  const handleMeasurements = go(() => {
    navigate('/')
    window.dispatchEvent(new Event('phoenix:open-measurements'))
  })
  const handlePhotos = go(() => {
    navigate('/')
    window.dispatchEvent(new Event('phoenix:open-photos'))
  })
  // Water lives as a phoenix tab (state-driven, not a route). Going
  // through navigate(state.tab) lets PhoenixTracker's useEffect on
  // location.state.tab pick it up and switch panes — same path the
  // bottom-bar tap used before water moved into this drawer.
  const handleProgress = go(() => navigate('/', { state: { tab: 'progress' } }))
  const handleHabits = go(() => navigate('/habits'))
  const handlePhysique = go(() => navigate('/physique'))
  const handleSleep = go(() => navigate('/sleep'))
  const handleGoalPace = go(() => navigate('/goal-pace'))
  const handlePhases = go(() => navigate('/phases'))
  const handleHealth = go(() => navigate('/health'))
  const handleFinance = go(() => navigate('/finance'))
  const handleNews = go(() => navigate('/news'))
  const handleSocial = go(() => navigate('/social'))
  const handleTech = go(() => navigate('/tech'))
  const handleYouTube = go(() => navigate('/youtube'))
  const handleSports = go(() => navigate('/sports'))
  const handleReports = go(() => navigate('/reports'))
  const handleJourney = go(() => navigate('/journey'))
  const handleLearning = go(() => navigate('/learning'))
  const handleEnglish = go(() => navigate('/english'))
  const handleImmigration = go(() => navigate('/immigration'))
  const handleWordle = go(() => navigate('/games/wordle'))
  const handleQuestions = go(() => navigate('/games/questions'))
  const handleQuiz = go(() => navigate('/games/quiz'))
  const handleSpy = go(() => navigate('/games/spy'))
  const handleMessages = go(() => navigate('/messages'))
  const handleFriends = go(() => navigate('/friends'))
  const handleLifeSearch = go(() => navigate('/search'))
  const handlePeople = go(() => navigate('/people'))
  const handleWishlist = go(() => navigate('/wishlist'))

  const name = profile?.name?.trim() || t('profile.fallbackName')
  const age = computeAge(profile?.birthday)
  const days = daysUntilBirthday(profile?.birthday)
  const userIdShort = user?.id ? `${user.id.slice(0, 8)}…` : '—'

  if (!mounted) return null
  if (typeof document === 'undefined') return null

  // Grid items. Two columns of square-ish tiles fill the drawer
  // space much more nicely than a single column of text rows — and
  // a touchable tile is easier to hit than a thin row on mobile.
  // Order is roughly "daily" → "weekly" → "settings/system" so the
  // most-used items sit at the top of the grid.
  const tiles = [
    { icon: Sparkles,     label: t('assistant.tile'),                onClick: handleAssistant,    tone: 'primary' },
    // AI life search — natural-language retrieval across every module.
    { icon: Radar,        label: t('lifeSearch.title'),              onClick: handleLifeSearch,   tone: 'primary' },
    { icon: TrendingUp,   label: t('phoenixTracker.tabs.progress'),  onClick: handleProgress,     tone: 'primary' },
    { icon: CheckCircle2, label: t('habits.title'),                onClick: handleHabits,       tone: 'emerald' },
    { icon: ScanLine,     label: t('physique.title'),              onClick: handlePhysique,     tone: 'primary' },
    { icon: Ruler,        label: t('profile.measurements'),         onClick: handleMeasurements, tone: 'primary' },
    { icon: Camera,       label: t('phoenixTracker.tabs.photos'),   onClick: handlePhotos,       tone: 'primary' },
    { icon: Layers,       label: t('phoenixTracker.phases.title'),  onClick: handlePhases,       tone: 'primary' },
    { icon: Stethoscope,  label: t('healthProfile.title'),          onClick: handleHealth,       tone: 'emerald' },
    { icon: Wallet,       label: t('finance.title'),                onClick: handleFinance,      tone: 'emerald' },
    // Wishlist — finance-adjacent want-to-buy with AI timing advice.
    { icon: Gift,         label: t('wishlist.title'),               onClick: handleWishlist,     tone: 'emerald' },
    { icon: Newspaper,    label: t('news.title'),                   onClick: handleNews,         tone: 'primary' },
    { icon: Users,        label: t('social.title'),                 onClick: handleSocial,       tone: 'primary' },
    { icon: Cpu,          label: t('tech.title'),                   onClick: handleTech,         tone: 'primary' },
    { icon: Youtube,      label: t('news.youtube.title'),           onClick: handleYouTube,      tone: 'red' },
    { icon: Trophy,       label: t('sports.title'),                 onClick: handleSports,       tone: 'amber' },
    { icon: FileText,     label: t('reports.title'),                onClick: handleReports,      tone: 'primary' },
    // Spy party game — open to every signed-in user (pass-and-play,
    // no pair gating), so it sits outside the hasOwnerAccess block.
    { icon: VenetianMask, label: t('games.spy.title'),              onClick: handleSpy,          tone: 'primary' },
    // Friends graph — open to every signed-in user; the friend-scoped
    // features (messages, Wordle results, Spy invites) key off it.
    { icon: Users2,       label: t('friends.title'),                onClick: handleFriends,      tone: 'primary' },
    // People CRM — birthdays + gone-quiet nudges for the few that matter.
    { icon: Contact,      label: t('people.title'),                 onClick: handlePeople,       tone: 'primary' },
    // Wordle opened to all users with the friends system — the
    // scoreboard shows each accepted friend's daily result.
    { icon: Gamepad2,     label: t('games.wordle.title'),           onClick: handleWordle,       tone: 'primary' },
    // Direct-messages inbox — open to every signed-in user; sends are
    // gated to accepted friends (RLS + send-custom-push).
    { icon: Inbox,        label: t('messages.list.title'),          onClick: handleMessages,     tone: 'primary', badge: unreadMessages },
    // Journey is the only menu item that stays single-owner — it's the
    // private recovery log, surfaced nowhere else. Learning / English /
    // Immigration are owner-tier features the owner has explicitly
    // opened to the trusted-guest list.
    ...(isOwner(user) ? [
      { icon: Compass,   label: t('journey.title'),    onClick: handleJourney,    tone: 'primary' },
      { icon: Moon,      label: t('sleep.title'),      onClick: handleSleep,      tone: 'primary' },
      { icon: Target,    label: t('goalPace.title'),   onClick: handleGoalPace,   tone: 'primary' },
    ] : []),
    ...(hasOwnerAccess(user) ? [
      { icon: BookOpen,  label: t('learning.title'),   onClick: handleLearning,   tone: 'primary' },
      { icon: Languages, label: t('english.title'),    onClick: handleEnglish,    tone: 'primary' },
      { icon: Plane,     label: t('immigration.title'), onClick: handleImmigration, tone: 'primary' },
      { icon: MessagesSquare, label: t('games.questions.title'), onClick: handleQuestions,  tone: 'primary' },
      { icon: Brain,          label: t('games.quiz.title'),      onClick: handleQuiz,       tone: 'primary' },
    ] : []),
    { icon: SettingsIcon, label: t('settings.title'),               onClick: handleOpenSettings, tone: 'muted' },
  ]

  return createPortal(
    <>
      {/* Scrim — fades in/out independently of the sheet's slide. */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm',
          'transition-opacity ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        style={{ transitionDuration: `${visible ? ENTER_MS : EXIT_MS}ms` }}
        onClick={handleScrimClick}
        aria-hidden="true"
      />
      {/* The sheet itself. translate-y-full hides off the bottom
          edge; translate-y-0 is the rest position. ease-out gives the
          natural "fast in, soft landing" sheet feel. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('profile.openMenu')}
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 mx-auto max-w-screen-sm',
          'rounded-t-3xl overflow-hidden will-change-transform',
          'bg-white/85 dark:bg-black/60',
          'backdrop-blur-2xl backdrop-saturate-200',
          'border-t border-x border-white/40 dark:border-white/10',
          'shadow-[0_-12px_30px_-10px_rgba(0,0,0,0.35)]',
          'transition-transform ease-out',
          'flex flex-col max-h-[85vh]',
          // While dragging we drive the transform inline; otherwise the
          // show/hide translate classes do.
          dragRef.current.active ? '' : (visible ? 'translate-y-0' : 'translate-y-full'),
        )}
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          transitionDuration: dragRef.current.active ? '0ms' : `${visible ? ENTER_MS : EXIT_MS}ms`,
          transform: dragY !== 0 ? `translateY(${dragY}px)` : undefined,
        }}
      >
        {/* Grab handle — drag it to pull the sheet down and dismiss,
            like a native bottom sheet. Bigger hit area than the visible
            pill so it's easy to grab. touch-action:none so the drag
            doesn't get hijacked by the browser's scroll. */}
        <div
          className="pt-2 pb-2 flex justify-center flex-shrink-0 cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'none' }}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          onTouchCancel={onDragEnd}
        >
          <div className="w-12 h-1.5 rounded-full bg-foreground/25" />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {/* Greeting — first to cascade in (index 0). Avatar lives
              alongside the greeting as its own UI element, NOT inline
              with {{name}} — per the owner's "no avatar when the name
              is in a sentence" rule. */}
          <CascadeItem visible={visible} index={0}>
            <div className="px-4 py-3.5 border-b border-border/60 flex items-center gap-3" dir="auto">
              <button
                type="button"
                onClick={() => { onClose(); navigate('/profile') }}
                className="relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-500/40 flex-shrink-0"
                aria-label={t('profile.editPage.title')}
              >
                <Avatar userId={user?.id} profile={profile} size={44} />
                <span className="absolute -bottom-0.5 -end-0.5 w-4 h-4 rounded-full bg-app-primary-500 text-white border-2 border-card inline-flex items-center justify-center">
                  <Camera className="w-2.5 h-2.5" />
                </span>
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground" dir="auto">
                  {t('profile.greeting', { name })}
                </p>
                {age != null && days != null && (
                  <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
                    {days === 0
                      ? t('profile.ageLineToday', { age })
                      : t('profile.ageLine', { age, days })}
                  </p>
                )}
                {!profile?.birthday && (
                  <button
                    onClick={handleCompleteProfile}
                    className="text-xs text-app-primary-500 hover:underline mt-1"
                  >
                    {t('profile.completeProfile')}
                  </button>
                )}
              </div>
            </div>
          </CascadeItem>

          {/* User ID with copy */}
          <CascadeItem visible={visible} index={1}>
            <button
              onClick={handleCopy}
              className="w-full px-4 py-3 flex items-center gap-2 hover:bg-accent transition-colors border-b border-border/60 text-start"
            >
              <span className="text-xs text-muted-foreground">{t('profile.userId')}</span>
              <span className="text-xs font-mono text-foreground truncate flex-1 ms-1" dir="ltr">
                {userIdShort}
              </span>
              {copied ? (
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
              ) : (
                <Copy className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}
            </button>
          </CascadeItem>

          {/* 2-column tile grid. Fills the drawer's vertical space
              with touchable squares instead of skinny text rows;
              easier to hit on mobile and pleasanter to scan. Each
              tile gets the same per-index cascade entrance — the
              grid order is row-major so the top-left tile lands
              first and the bottom-right last, which reads naturally
              as "the menu is settling in". */}
          <div className="grid grid-cols-3 gap-2.5 p-3">
            {tiles.map((tile, i) => (
              <CascadeItem key={tile.label} visible={visible} index={i + 2}>
                <SheetTile {...tile} />
              </CascadeItem>
            ))}
          </div>

          {/* Sign-out gets its own row, full-width, with a red tone
              so it reads as the destructive action separate from the
              tile grid. */}
          <CascadeItem visible={visible} index={tiles.length + 2}>
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-3 mt-1 flex items-center justify-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-border/60 text-start"
            >
              <LogOut className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-500 font-medium" dir="auto">{t('profile.signOut')}</span>
            </button>
          </CascadeItem>

          {/* Ambient credits. Pulled out of the per-page footer (which
              was forcing short pages like /games/wordle into a scroll
              just to surface "made with ❤️ + version + socials") and
              relocated here so it's still findable but no longer
              competes for viewport space on every screen. */}
          <CascadeItem visible={visible} index={tiles.length + 3}>
            <SheetCredits t={t} />
          </CascadeItem>
        </div>
      </div>
    </>,
    document.body,
  )
}

function SheetCredits({ t }) {
  const year = new Date().getFullYear()
  return (
    <div className="px-4 py-3 border-t border-border/60 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1.5 text-[11px] text-muted-foreground" dir="auto">
      <span className="flex items-center gap-1">
        {t('footer.madeWith')} <Heart className="w-3 h-3 text-red-500 fill-red-500" /> {t('footer.by')}{' '}
        <a
          href="https://github.com/omarMohamed02"
          target="_blank"
          rel="noopener noreferrer"
          className="text-app-primary-500 hover:text-app-primary-600 font-medium transition-colors"
        >
          Omar Mohamed
        </a>
      </span>
      <span className="text-border">|</span>
      <div className="flex items-center gap-1">
        <a
          href="https://github.com/omarMohamed03"
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="GitHub"
        >
          <Github className="w-3.5 h-3.5" />
        </a>
        <a
          href="https://www.linkedin.com/in/omarkamalmohamed/"
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="LinkedIn"
        >
          <Linkedin className="w-3.5 h-3.5" />
        </a>
      </div>
      <span className="text-border">|</span>
      <span className="text-muted-foreground/60 font-mono" dir="ltr">© {year} · v{APP_VERSION} · {APP_BUILD}</span>
    </div>
  )
}

// Each row enters from a small downward offset + opacity 0, with a
// per-index delay so the rows cascade in as the sheet finishes its
// slide-up. On exit they all snap back instantly — staggered exit
// feels sluggish when the user is dismissing.
function CascadeItem({ visible, index, children }) {
  const enterDelay = index * ROW_STAGGER_MS
  return (
    <div
      className={cn(
        'transition-all ease-out',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
      )}
      style={{
        transitionDuration: `${visible ? 220 : 80}ms`,
        transitionDelay: visible ? `${enterDelay}ms` : '0ms',
      }}
    >
      {children}
    </div>
  )
}

// One tile in the 2-col grid. Square-ish touchable block with a
// tinted icon on top and the label below. `tone` colors the icon
// only; the tile background stays neutral so a grid of tiles reads
// as a coherent surface rather than a riot of colored squares.
const TONE_CLASSES = {
  primary: 'text-app-primary-500',
  red: 'text-red-500',
  emerald: 'text-emerald-500',
  amber: 'text-amber-500',
  cyan: 'text-cyan-500',
  muted: 'text-muted-foreground',
}

function SheetTile({ icon: Icon, label, onClick, tone = 'primary', badge = 0 }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        // w-full so the button fills its grid cell. aspect-square
        // makes every tile the exact same footprint regardless of
        // label length — app-icon style.
        'w-full aspect-square',
        'group flex flex-col items-center justify-center gap-3 p-4 rounded-2xl',
        'bg-foreground/[0.04] hover:bg-foreground/[0.08] active:scale-[0.97] transition-all',
      )}
    >
      {/* Bigger icon + label sized to match the square's actual
          footprint — the previous w-7 + text-xs looked dwarfed
          inside the ~160px tile on iPhone. */}
      <div className="relative flex-shrink-0">
        <Icon
          className={cn(
            'w-11 h-11 sm:w-12 sm:h-12',
            TONE_CLASSES[tone] || TONE_CLASSES.primary,
          )}
        />
        {badge > 0 && (
          <span className="absolute -top-1.5 -end-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span
        className="text-sm font-semibold text-foreground text-center line-clamp-2 max-w-full leading-tight"
        dir="auto"
      >
        {label}
      </span>
    </button>
  )
}
